import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import API_URL from '../config/config';
import { editProgressPolicy, mergeEditProgress } from '../utils/orderEditProgress';

export default function useOrderEditProgress(order) {
  const current = useRef({});
  const revision = useRef(0);
  const alive = useRef(false);
  const [progress, setProgress] = useState({});
  const id = order?._id;
  const publicId = order?.orderId;
  const identity = useRef(id);
  identity.current = id;
  const apply = useCallback((payload) => {
    if (!alive.current || identity.current !== id) return;
    const next = mergeEditProgress(current.current, payload, [id, publicId].filter(Boolean).map(String));
    if (next === current.current) return;
    const before = editProgressPolicy(current.current);
    const after = editProgressPolicy(next);
    if (before.blocked !== after.blocked || before.parcelLocked !== after.parcelLocked) revision.current++;
    current.current = next; // Async payment continuations must see this immediately.
    setProgress(next);
  }, [id, publicId]);

  const refresh = useCallback(async () => {
    if (!id) return false;
    try {
      const response = await axios.get(`${API_URL}/order/customer_app/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        timeout: 15000,
      });
      if (!alive.current || identity.current !== id || !response.data?.order) return false;
      apply(response.data.order);
      return true;
    } catch (_) { return false; }
  }, [id, apply]);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    current.current = {};
    revision.current++;
    setProgress({});
  }, [id]);

  useEffect(() => { if (order) apply(order); }, [order, apply]);

  useEffect(() => {
    if (!id) return undefined;
    let disposed = false;
    const socket = io(API_URL.current, { transports: ['websocket', 'polling'], forceNew: true });
    const receive = (payload) => { if (!disposed) apply(payload); };
    const join = () => {
      [id, publicId].filter(Boolean).forEach((orderId) => socket.emit('track_order', { orderId }));
      refresh();
    };
    const resume = () => { if (document.visibilityState === 'visible') refresh(); };
    socket.on('connect', join);
    socket.on('order_status_update', receive);
    socket.on('customer_track_progress', receive);
    document.addEventListener('visibilitychange', resume);
    if (socket.connected) join();
    return () => {
      disposed = true;
      socket.off('connect', join);
      socket.off('order_status_update', receive);
      socket.off('customer_track_progress', receive);
      socket.disconnect();
      document.removeEventListener('visibilitychange', resume);
    };
  }, [id, publicId, apply, refresh]);

  return { ...editProgressPolicy(progress), current, revision, refresh, alive };
}
